import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/api/client';
import {
  LayoutDashboard, Image, PlusCircle, MessageSquare, User,
  BarChart2, ClipboardList, Users, Tag, Layers, Settings, FileText, Bell,
  Menu, LogOut, ShieldCheck, ChevronRight, Receipt, CreditCard
} from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

const MEMBER_NAV = [
  { path: '/dashboard', label: 'Overview', icon: LayoutDashboard, exact: true },
  { path: '/dashboard/listings', label: 'My Listings', icon: Image },
  { path: '/dashboard/listings/create', label: 'Create Listing', icon: PlusCircle },
  { path: '/dashboard/enquiries', label: 'Enquiries', icon: MessageSquare },
  { path: '/dashboard/transactions', label: 'Transactions', icon: Receipt },
  { path: '/dashboard/profile', label: 'Account Settings', icon: User },
];

const ADMIN_NAV = [
  { path: '/admin', label: 'Analytics', icon: BarChart2, exact: true, group: 'Overview' },
  { path: '/admin/applications', label: 'Applications', icon: ClipboardList, group: 'People' },
  { path: '/admin/members', label: 'Members', icon: Users, group: 'People' },
  { path: '/admin/listings', label: 'All Listings', icon: Image, group: 'Content' },
  { path: '/admin/conversations', label: 'Conversations', icon: MessageSquare, group: 'Content' },
  { path: '/admin/transactions', label: 'Transactions', icon: CreditCard, group: 'Content' },
  { path: '/admin/site-content', label: 'Site Content', icon: FileText, group: 'Content' },
  { path: '/admin/notifications', label: 'Notifications', icon: Bell, group: 'Content' },
  { path: '/admin/categories', label: 'Categories', icon: Tag, group: 'Settings' },
  { path: '/admin/listing-types', label: 'Listing Types', icon: Layers, group: 'Settings' },
  { path: '/admin/blocked-words', label: 'Blocked Words', icon: ShieldCheck, group: 'Settings' },
  { path: '/admin/config', label: 'Config', icon: Settings, group: 'Settings' },
];

function AdminSidebarContent({ user, logout, location, navigate }) {
  const groups = ['Overview', 'People', 'Content', 'Settings'];
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    api.get('/admin/stats/pending-listings')
      .then(r => setPendingCount(r.data.count || 0))
      .catch(() => {});
  }, [location.pathname]); // re-fetch when navigating
  return (
    <div className="flex flex-col h-full" style={{ backgroundColor: '#1C1C1C' }}>
      {/* Brand */}
      <div className="px-5 py-5" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Link to="/" className="block">
          <div className="flex items-center gap-2.5">
            <div
              className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: '#FFFFFF' }}
            >
              <span style={{ fontFamily: 'var(--font-serif)', fontSize: '0.75rem', fontWeight: 700, color: '#1C1C1C' }}>TL</span>
            </div>
            <div>
              <span style={{ fontFamily: 'var(--font-serif)', fontSize: '0.9rem', fontWeight: 600, color: '#F5F0E8' }}>
                The Listing Room
              </span>
              <p className="text-[10px] mt-0" style={{ color: 'rgba(255,255,255,0.8)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Admin Console</p>
            </div>
          </div>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-3 overflow-y-auto" data-testid="admin-sidebar">
        {groups.map(group => {
          const items = ADMIN_NAV.filter(i => i.group === group);
          return (
            <div key={group} className="mb-4">
              <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.25)' }}>
                {group}
              </p>
              {items.map(item => {
                const isActive = item.exact
                  ? location.pathname === item.path
                  : location.pathname.startsWith(item.path);
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`sidebar-item-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                    className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm mb-0.5 transition-colors"
                    style={{
                      color: isActive ? '#F5F0E8' : 'rgba(245,240,232,0.5)',
                      backgroundColor: isActive ? 'rgba(255,255,255,0.15)' : 'transparent',
                      borderLeft: isActive ? '2px solid #FFFFFF' : '2px solid transparent',
                      fontWeight: isActive ? 500 : 400,
                    }}
                  >
                    <Icon size={15} style={{ color: isActive ? '#FFFFFF' : 'rgba(245,240,232,0.4)', flexShrink: 0 }} />
                    <span className="flex-1">{item.label}</span>
                    {item.label === 'All Listings' && pendingCount > 0 && (
                      <span
                        className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                        style={{ backgroundColor: '#F59E0B', color: '#1C1C1C', minWidth: '18px', textAlign: 'center' }}
                        data-testid="pending-review-badge"
                      >
                        {pendingCount > 99 ? '99+' : pendingCount}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* User */}
      <div className="px-3 py-4" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl mb-2"
          style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0"
            style={{ backgroundColor: '#FFFFFF', color: '#1C1C1C' }}>
            {user?.name?.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium truncate" style={{ color: '#F5F0E8' }}>{user?.name}</p>
            <p className="text-[10px] truncate" style={{ color: 'rgba(245,240,232,0.4)' }}>{user?.email}</p>
          </div>
        </div>
        <button
          onClick={() => { logout(); navigate('/'); }}
          className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm w-full transition-colors hover:bg-red-900/20"
          style={{ color: 'rgba(245,240,232,0.4)' }}
          data-testid="sidebar-logout"
        >
          <LogOut size={14} />
          Sign out
        </button>
      </div>
    </div>
  );
}

function MemberSidebarContent({ user, logout, location, navigate }) {
  return (
    <div className="flex flex-col h-full" style={{ backgroundColor: 'var(--surface)' }}>
      {/* Brand */}
      <div className="px-5 py-5 border-b" style={{ borderColor: 'var(--border-clr)' }}>
        <Link to="/" className="block">
          <span style={{ fontFamily: 'var(--font-serif)', fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>
            The Listing Room
          </span>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>Member Portal</p>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto" data-testid="member-sidebar">
        {MEMBER_NAV.map((item) => {
          const isActive = item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path);
          const Icon = item.icon;
          return (
            <Link
              key={item.path}
              to={item.path}
              className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm mb-0.5 transition-colors"
              style={{
                color: isActive ? 'var(--text)' : 'var(--text-muted)',
                backgroundColor: isActive ? 'var(--bg-subtle)' : 'transparent',
                borderLeft: isActive ? '2px solid var(--gold)' : '2px solid transparent',
                fontWeight: isActive ? 500 : 400,
              }}
              data-testid={`sidebar-item-${item.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <Icon size={16} style={{ color: isActive ? 'var(--gold)' : 'var(--text-muted)', flexShrink: 0 }} />
              {item.label}
            </Link>
          );
        })}
      </nav>

      {/* User */}
      <div className="px-3 py-4 border-t" style={{ borderColor: 'var(--border-clr)' }}>
        <div className="flex items-center gap-3 px-2 py-2 mb-2 rounded-xl" style={{ backgroundColor: 'var(--bg-subtle)' }}>
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0" style={{ backgroundColor: 'var(--gold)' }}>
            {user?.name?.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>{user?.name}</p>
            <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>{user?.email}</p>
          </div>
        </div>
        <button
          onClick={() => { logout(); navigate('/'); }}
          className="flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm w-full transition-colors hover:bg-red-50"
          style={{ color: '#B42318' }}
          data-testid="sidebar-logout"
        >
          <LogOut size={15} />
          Sign out
        </button>
      </div>
    </div>
  );
}

export default function DashboardShell({ children, title }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isAdmin = user?.role === 'admin';

  // Force Segoe UI across the entire admin area (incl. portaled dialogs/menus)
  useEffect(() => {
    if (isAdmin) {
      document.body.classList.add('admin-theme');
      return () => document.body.classList.remove('admin-theme');
    }
  }, [isAdmin]);

  const SidebarComp = isAdmin ? AdminSidebarContent : MemberSidebarContent;

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: isAdmin ? '#F7F6F3' : 'var(--bg)' }}>
      {/* Desktop sidebar */}
      <aside
        className="hidden lg:flex flex-col flex-shrink-0"
        style={{ width: '240px', backgroundColor: isAdmin ? '#1C1C1C' : 'var(--surface)' }}
      >
        <SidebarComp user={user} logout={logout} location={location} navigate={navigate} />
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header
          className="flex items-center gap-4 px-6 flex-shrink-0"
          style={{
            height: '56px',
            borderBottom: isAdmin ? '1px solid rgba(0,0,0,0.06)' : '1px solid var(--border-clr)',
            backgroundColor: isAdmin ? '#FFFFFF' : 'var(--surface)',
            boxShadow: isAdmin ? '0 1px 3px rgba(0,0,0,0.04)' : 'none',
          }}
        >
          {/* Mobile menu */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button className="lg:hidden" style={{ color: 'var(--text)' }}>
                <Menu size={20} />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0" style={{ width: '240px' }}>
              <SidebarComp
                user={user}
                logout={logout}
                location={location}
                navigate={(p) => { navigate(p); setMobileOpen(false); }}
              />
            </SheetContent>
          </Sheet>

          {title && (
            <div className="flex items-center gap-2">
              {isAdmin && (
                <div className="hidden sm:flex items-center gap-1.5 text-xs" style={{ color: 'rgba(0,0,0,0.35)' }}>
                  <span>Admin</span>
                  <ChevronRight size={12} />
                </div>
              )}
              <h1
                className="text-base font-semibold"
                style={{ fontFamily: 'var(--font-serif)', color: isAdmin ? '#1C1C1C' : 'var(--text)' }}
              >
                {title}
              </h1>
            </div>
          )}

          <div className="ml-auto flex items-center gap-3">
            {isAdmin ? (
              <span className="hidden sm:inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full"
                style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: '#FFFFFF', fontWeight: 500 }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#FFFFFF' }} />
                Admin
              </span>
            ) : (
              <Link to="/browse" className="text-xs hidden sm:block" style={{ color: 'var(--text-muted)' }}>Browse →</Link>
            )}
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold"
              style={{ backgroundColor: isAdmin ? '#FFFFFF' : 'var(--gold)', color: isAdmin ? '#1C1C1C' : 'white' }}
            >
              {user?.name?.charAt(0).toUpperCase()}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}
